import { EntryResource, fdk } from 'ec.fdk';
import { Article } from './article';
import { getAuth } from './auth';
import { Customer } from './customer';
import { fetchWithVersion } from './utils';

const env = import.meta.env.VITE_ENV;
const appsiteURL = import.meta.env.VITE_APPSITE_URL;

export function shortenUUID(uuid: string, factor = 2) {
  let shortUUID: string | string[] | number[];
  let validatedFactor;
  if (
    !/^[0-9A-F]{8}-[0-9A-F]{4}-4[0-9A-F]{3}-[89AB][0-9A-F]{3}-[0-9A-F]{12}$/i.test(uuid) ||
    (factor && typeof factor !== 'number')
  ) {
    throw new Error('invalid parameter format');
  }
  if (!factor || factor < 1 || factor > 5) {
    validatedFactor = 1;
  } else {
    validatedFactor = factor;
  }
  shortUUID = uuid.replace(/-/g, '');
  shortUUID = shortUUID.split('');
  shortUUID = shortUUID.map((element) => parseInt(element, 16));
  let j;
  let l: number;

  function xor(val: number, index: number) {
    return val ^ (shortUUID as number[])[l / 2 + index];
  }

  for (j = 0; j < validatedFactor; j++) {
    l = shortUUID.length;
    shortUUID = shortUUID.slice(0, l / 2).map(xor);
  }
  shortUUID = shortUUID.map((element) => element.toString(16));
  shortUUID = shortUUID.join('');
  return shortUUID;
}

export async function getAppID(shortID: string) {
  const publicApi = await fdk(env).dm(shortID).publicApi();
  return publicApi.config.appID;
}

export async function getInternalAppsiteUrl(shortID: string) {
  const appID = await getAppID(shortID);
  return `${appsiteURL}/live/${shortenUUID(appID)}`;
}

declare type SettingsEntry = EntryResource & {
  subdomain: string;
  appContent: Array<{ type: string; shortID: string }>;
};

const settingsPromise: Map<string, Promise<SettingsEntry>> = new Map();

export async function getSettings(shortID: string) {
  if (!settingsPromise.has(shortID)) {
    const promise = getInternalAppsiteUrl(shortID)
      .then((appsiteURL) => {
        const url = `${appsiteURL}/api/settings`;
        return fetchWithVersion(url);
      })
      .then((res) => res.json());
    settingsPromise.set(shortID, promise);
    return promise;
  }
  return settingsPromise.get(shortID);
}

export async function getCustomer(shortID: string): Promise<Customer | null> {
  const appsiteURL = await getInternalAppsiteUrl(shortID);
  const { accessToken } = (await getAuth(shortID)) ?? {};

  if (!accessToken) {
    return null;
  }

  const res = await fetchWithVersion(`${appsiteURL}/api/me/account`, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'X-Requested-With': 'checkinapp',
      Accept: 'application/json',
    },
  });
  if (!res.ok) {
    return null;
  }

  const { errors, me } = await res.json();

  if (errors) {
    return null;
  }

  return me;
}

const articleCache = new Map<string, Article>();

export async function getArticle(shortID: string, articleID: string, token: string) {
  const cacheKey = `${shortID}-${articleID}`;
  if (articleCache.has(cacheKey)) {
    return articleCache.get(cacheKey);
  }
  const appsiteURL = await getInternalAppsiteUrl(shortID);
  const url = `${appsiteURL}/api/appArticles/content/${articleID}`;
  const result = await fetchWithVersion(url, { headers: { Authorization: `Bearer ${token}` } }).then((res) =>
    res.json(),
  );
  articleCache.set(cacheKey, result);
  return result;
}
